import {
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  EventEmitter,
  inject,
  input,
  OnDestroy,
  OnInit,
  Output,
  signal,
  ViewChild
} from '@angular/core';
import {
  autoUpdate,
  computePosition,
  flip,
  offset,
  shift
} from '@floating-ui/dom';
import {
  DateRangeSelection,
  FiscalMonth
} from '@design-system/components/m-fiscal-calendar/m-fiscal-calendar.component.types';
import { CompareService } from '@portal/app/shared/services/compare.service';
import { DateTimeService } from '@portal/app/shared/services/date-time.service';
import { DateRange } from '@portal/app/shared/types';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);

type InputDateType =
  | 'startDate'
  | 'endDate'
  | 'startDateCompare'
  | 'endDateCompare';

type SelectionSource = 'calendar' | 'input';

@Component({
  selector: 'portal-fiscal-calendar',
  templateUrl: './portal-fiscal-calendar.component.html',
  styleUrls: ['./portal-fiscal-calendar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PortalFiscalCalendarComponent implements OnInit, OnDestroy {
  @ViewChild('calendarInput', { static: true })
  calendarInput!: ElementRef<HTMLInputElement>;

  @ViewChild('calendarPopup', { static: true })
  calendarPopup!: ElementRef<HTMLElement>;

  readonly label = input('Time Period');
  readonly startDate = input<string>('');
  readonly endDate = input<string>('');
  readonly previousStartDate = input<string | null>(null);
  readonly previousEndDate = input<string | null>(null);
  readonly months = input<FiscalMonth[]>([]);
  readonly minDate = input<dayjs.Dayjs | undefined>(undefined);
  readonly maxDate = input<dayjs.Dayjs | undefined>(undefined);

  @Output() readonly dateChanged = new EventEmitter<{
    startDate: string;
    endDate: string;
    previousStartDate: string | null;
    previousEndDate: string | null;
  }>();

  showCalendar = false;
  compareToggledOn = false;
  compareEnabled = true;
  availabilityInfoText = '';

  private dateToShowSingleRange = '';
  private dateToShowCompareRange = '';

  readonly selected = signal<DateRange>(this.createDefaultDateRange());
  readonly selectedCompare = signal<DateRange>(this.createDefaultDateRange());

  readonly displayDates = computed(() => {
    const selectedRange = this.selected();
    const compareRange = this.selectedCompare();
    const formatDate = (date: dayjs.Dayjs) => date.format(this.displayFormat);

    return {
      start: formatDate(selectedRange.startDate),
      end: formatDate(selectedRange.endDate),
      prevStart: formatDate(compareRange.startDate),
      prevEnd: formatDate(compareRange.endDate)
    };
  });

  readonly singleRangeLabel = computed(() => {
    const dates = this.displayDates();
    return `${dates.start} - ${dates.end}`;
  });

  readonly compareRangeLabel = computed(() => {
    const dates = this.displayDates();
    return `${dates.start} - ${dates.end} | ${dates.prevStart} - ${dates.prevEnd}`;
  });

  readonly calendarDates = computed(() => {
    const selected = this.selected();
    const selectedCompare = this.selectedCompare();
    return {
      start: this.formatCalendarDate(selected.startDate),
      end: this.formatCalendarDate(selected.endDate),
      compareStart: this.formatCalendarDate(selectedCompare.startDate),
      compareEnd: this.formatCalendarDate(selectedCompare.endDate)
    };
  });

  readonly formattedMinDate = computed(() =>
    this.formatDateBound(this.minDate())
  );

  readonly formattedMaxDate = computed(() =>
    this.formatDateBound(this.maxDate())
  );

  private formatDateBound(date: dayjs.Dayjs | undefined): string | null {
    return date ? date.format(this.calendarDateFormat) : null;
  }

  private cachedSelected: DateRange = this.createDefaultDateRange();
  private cachedSelectedCompare: DateRange = this.createDefaultDateRange();
  private cachedCompareToggledOn = false;

  private cleanupPopup?: () => void;
  private isApplying = false;
  private lastSelectionSource: SelectionSource = 'input';
  private readonly displayFormat = 'MMM D, YYYY';
  private readonly editFormat = 'MM-DD-YYYY';
  private readonly calendarDateFormat = 'YYYY-MM-DD';

  private readonly dateService = inject(DateTimeService);
  private readonly compareService = inject(CompareService);

  ngOnInit(): void {
    this.compareToggledOn = this.compareService.compare;
    this.compareEnabled = this.compareService.compareIsAvailable;
    this.availabilityInfoText = `Latest complete data available from ${this.formatDate(
      this.dateService.maximumEndDate(),
      'MMMM D, YYYY'
    )}`;

    this.initializeDatesFromInputs();
    this.updateAppliedLabels();
  }

  ngOnDestroy(): void {
    this.cleanupPopup?.();
  }

  private initializeDatesFromInputs(): void {
    if (this.startDate() && this.endDate()) {
      this.selected.set(this.createDateRange(this.startDate(), this.endDate()));
    }

    const prevStart = this.previousStartDate();
    const prevEnd = this.previousEndDate();
    if (prevStart && prevEnd) {
      this.selectedCompare.set(this.createDateRange(prevStart, prevEnd));
    }
  }

  toggleCalendar(event?: MouseEvent): void {
    event?.preventDefault();
    this.showCalendar ? this.closeCalendar() : this.openCalendar();
  }

  onDialogShow(): void {
    this.cleanupPopup?.();
    this.setupPopupPositioning();
  }

  onOutsideCompareToggle(value: boolean): void {
    this.cacheAppliedValues();
    this.compareToggledOn = value;
    this.openCalendar(false);
  }

  getDatePickerLabel(): string {
    return this.compareToggledOn
      ? this.dateToShowCompareRange
      : this.dateToShowSingleRange;
  }

  private updateAppliedLabels(): void {
    this.dateToShowSingleRange = this.singleRangeLabel();
    this.dateToShowCompareRange = this.compareRangeLabel();
  }

  onFiscalDateSelected(event: DateRangeSelection): void {
    this.lastSelectionSource = 'calendar';
    this.handleCalendarSelection(event);
  }

  onInputFocus(event: FocusEvent, type: InputDateType): void {
    const input = event.target as HTMLInputElement;
    input.value = this.getDateForType(type).format(this.editFormat);
  }

  onInputBlur(event: FocusEvent, type: InputDateType): void {
    const input = event.target as HTMLInputElement;
    input.value = this.getDateForType(type).format(this.displayFormat);
  }

  private readonly dateTypeMap: Record<InputDateType, () => dayjs.Dayjs> = {
    startDate: () => this.selected().startDate,
    endDate: () => this.selected().endDate,
    startDateCompare: () => this.selectedCompare().startDate,
    endDateCompare: () => this.selectedCompare().endDate
  };

  private getDateForType(type: InputDateType): dayjs.Dayjs {
    return this.dateTypeMap[type]();
  }

  onInputChange(event: Event, type: InputDateType): void {
    this.lastSelectionSource = 'input';
    const parsed = this.parseInputDate(
      (event.target as HTMLInputElement).value
    );

    if (!parsed.isValid() || !this.isWithinBounds(parsed)) {
      return;
    }

    this.handleInputDate(parsed, type);
  }

  apply(): void {
    this.isApplying = true;

    if (this.lastSelectionSource === 'input') {
      this.syncDatesFromInputFields();
    }

    this.updateAppliedLabels();
    this.emitDateRangeData();
    this.closeCalendar();
  }

  cancel(): void {
    if (this.isApplying) {
      this.isApplying = false;
      return;
    }
    this.restoreCachedValues();
    this.closeCalendar();
  }

  private handleCalendarSelection(event: DateRangeSelection): void {
    this.selected.set(this.createDateRange(event.startDate, event.endDate));

    if (event.previousStartDate && event.previousEndDate) {
      this.selectedCompare.set(
        this.createDateRange(event.previousStartDate, event.previousEndDate)
      );
    }
  }

  private handleInputDate(date: dayjs.Dayjs, type: InputDateType): void {
    switch (type) {
      case 'startDate':
        this.selected.update((current) => ({
          startDate: date,
          endDate: date.isAfter(current.endDate) ? date : current.endDate
        }));
        break;

      case 'endDate':
        this.selected.update((current) => ({
          startDate: current.startDate,
          endDate: date.isBefore(current.startDate) ? current.startDate : date
        }));
        break;

      case 'startDateCompare':
        this.selectedCompare.update((current) =>
          date.isAfter(current.endDate)
            ? current
            : { startDate: date, endDate: current.endDate }
        );
        break;

      case 'endDateCompare':
        this.selectedCompare.update((current) =>
          date.isBefore(current.startDate)
            ? current
            : { startDate: current.startDate, endDate: date }
        );
        break;
    }
  }

  private syncDatesFromInputFields(): void {
    const dates = this.displayDates();

    const currentStart = this.parseInputDate(dates.start);
    const currentEnd = this.parseInputDate(dates.end);

    if (currentStart.isValid() && currentEnd.isValid()) {
      this.selected.set(this.createDateRange(currentStart, currentEnd));
    }

    if (this.compareToggledOn) {
      const compareStart = this.parseInputDate(dates.prevStart);
      const compareEnd = this.parseInputDate(dates.prevEnd);

      if (compareStart.isValid() && compareEnd.isValid()) {
        this.selectedCompare.set(
          this.createDateRange(compareStart, compareEnd)
        );
      }
    }
  }

  private snapshotState(source?: {
    current?: DateRange;
    compare?: DateRange;
    toggled?: boolean;
  }): void {
    this.cachedSelected = { ...(source?.current ?? this.selected()) };
    this.cachedSelectedCompare = {
      ...(source?.compare ?? this.selectedCompare())
    };
    this.cachedCompareToggledOn = source?.toggled ?? this.compareToggledOn;
  }

  private cacheCurrentValues(): void {
    this.snapshotState();
  }

  private cacheAppliedValues(): void {
    this.snapshotState({
      current: this.compareService.currentDate ?? undefined,
      compare: this.compareService.previousDate ?? undefined,
      toggled: this.compareService.compare
    });
  }

  private restoreCachedValues(): void {
    this.selected.set({ ...this.cachedSelected });
    this.selectedCompare.set({ ...this.cachedSelectedCompare });
    this.compareToggledOn = this.cachedCompareToggledOn;
  }

  private emitDateRangeData(): void {
    const dates = this.calendarDates();

    this.compareService.currentDate = this.selected();
    this.compareService.previousDate = this.selectedCompare();
    this.compareService.compare = this.compareToggledOn;
    this.compareService.setCompareValue(this.compareToggledOn);

    this.cacheCurrentValues();

    this.dateChanged.emit({
      startDate: dates.start,
      endDate: dates.end,
      previousStartDate: this.compareToggledOn ? dates.compareStart : null,
      previousEndDate: this.compareToggledOn ? dates.compareEnd : null
    });
  }

  private setupPopupPositioning(): void {
    const referenceEl = this.calendarInput.nativeElement;
    const popupEl = this.calendarPopup.nativeElement;

    this.cleanupPopup = autoUpdate(referenceEl, popupEl, () => {
      computePosition(referenceEl, popupEl, {
        placement: 'bottom-end',
        middleware: [offset(7), flip(), shift({ padding: 7 })]
      }).then(({ x, y }) => {
        Object.assign(popupEl.style, {
          left: `${x}px`,
          top: `${y}px`
        });
      });
    });
  } //TODO: Try to position the popup via css

  private openCalendar(cacheValues = true): void {
    if (this.showCalendar) return;
    this.showCalendar = true;
    if (cacheValues) this.cacheCurrentValues();
  }

  private closeCalendar(): void {
    this.cleanupPopup?.();
    this.showCalendar = false;
  }

  private isWithinBounds(date: dayjs.Dayjs): boolean {
    const min = this.minDate();
    const max = this.maxDate();
    if (min && date.isBefore(min, 'day')) return false;
    if (max && date.isAfter(max, 'day')) return false;
    return true;
  }

  private createDefaultDateRange(): DateRange {
    const now = dayjs().utc(true);
    return { startDate: now, endDate: now };
  }

  private createDateRange(
    start: string | Date | dayjs.Dayjs,
    end: string | Date | dayjs.Dayjs
  ): DateRange {
    return {
      startDate: this.toUtcDayjs(start),
      endDate: this.toUtcDayjs(end)
    };
  }

  private toUtcDayjs(value: string | Date | dayjs.Dayjs): dayjs.Dayjs {
    return dayjs.isDayjs(value) ? value.utc(true) : dayjs(value).utc(true);
  }

  private parseInputDate(value: string): dayjs.Dayjs {
    // Try edit format first (MM-DD-YYYY), then display format (MMM D, YYYY)
    const editParsed = dayjs(value, this.editFormat, true).utc(true);
    if (editParsed.isValid()) {
      return editParsed;
    }
    return dayjs(value, this.displayFormat).utc(true);
  }

  private formatCalendarDate(date: dayjs.Dayjs): string {
    return date.format(this.calendarDateFormat);
  }

  formatDate(date: dayjs.Dayjs, template = this.displayFormat): string {
    return date.format(template);
  }
}
